import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Radar as ScanIcon,
  Refresh as RefreshIcon,
  CloudDownload as FetchIcon,
  Calculate as AnalyzeIcon,
} from "@mui/icons-material";
import Cron from "react-cron-generator";
import "react-cron-generator/build/cron-builder.css";
import CronBuilderBox from "../../../components/CronBuilderBox";
import { qogitaApi } from "../../../services/api/omni/qogita";
import { keepaApi } from "../../../services/api/omni/keepa";
import { priceAlertsApi } from "../../../services/api/omni/priceAlerts";
import {
  loadWatchlistPrefs,
  QOGITA_STATUS_FILTERS,
  KEEPA_FETCH_STATUS_LABELS,
  resolveCronValue,
  saveWatchlistPrefs,
  formatWatchlistGtin,
  formatKeepaQuerySelection,
  parseKeepaQuerySelection,
  qogitaCatalogSearchUrl,
  watchlistAmazonUrl,
  KEEPA_NETTOPANNE_MARGIN_SUMMARY,
  formatTargetEkMarginCaption,
  estimateCronFiresPerDay,
  CATALOG_DOWNLOAD_MAX_PER_24H,
} from "./qogitaWatchlistPrefs";

const formatPrice = (value, currency = "EUR") => {
  if (value === null || value === undefined || value === "") return "—";
  return `${Number(value).toFixed(2)} ${currency || "EUR"}`;
};

const MarginTierInfo = ({ sx }) => (
  <Typography variant="caption" color="text.secondary" component="div" sx={sx}>
    Target EK: {KEEPA_NETTOPANNE_MARGIN_SUMMARY}
  </Typography>
);

const formatCents = (cents) => {
  if (cents === null || cents === undefined || cents < 0) return "—";
  return `${(cents / 100).toFixed(2)} EUR`;
};

const qogitaCatalogPrice = (item) =>
  item?.qogita_catalog_price ??
  (item?.wholesale_best_seller === "Qogita" ? item.wholesale_best_price : null);

const qogitaCatalogStock = (item) =>
  item?.qogita_catalog_stock ??
  (item?.wholesale_best_seller === "Qogita" ? item.wholesale_best_stock : null);

/** Max buy / watchlist target — Keepa EK when analyzed, else Qogita target. */
const formatTargetEk = (item) => {
  const value = item.calculated_ek_price ?? item.target_price;
  const currency = item.target_price_currency || "EUR";
  return formatPrice(value, currency);
};

const renderQogitaGtinLink = (item) => {
  const label = formatWatchlistGtin(item);
  const url = qogitaCatalogSearchUrl(item.gtin);
  if (!url) return label;
  return (
    <Link
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </Link>
  );
};

const renderAmazonLink = (item, label) => {
  const url = watchlistAmazonUrl(item);
  if (!url) return label;
  return (
    <Link
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </Link>
  );
};

const keepaEanCount = (item) => item?.keepa_ean_list?.length ?? 0;

const KeepaEanCountButton = ({ item, onOpen }) => {
  const total = keepaEanCount(item);
  if (total === 0) return "—";
  const inCatalog = item?.keepa_ean_catalog_gtins?.length ?? 0;
  const label =
    inCatalog > 0 ? `${total} (${inCatalog} catalog)` : String(total);
  return (
    <Button
      size="small"
      variant="text"
      sx={{ minWidth: 0, px: 0.5 }}
      onClick={(e) => {
        e.stopPropagation();
        onOpen(item);
      }}
    >
      {label}
    </Button>
  );
};

const renderRepricerInventory = (item, navigate) => {
  if (!item.repricer_article_id) return "—";
  const stock =
    item.repricer_stock === null || item.repricer_stock === undefined
      ? "—"
      : item.repricer_stock;
  const ek =
    item.repricer_brutto_buy_price != null
      ? formatPrice(item.repricer_brutto_buy_price)
      : "—";
  const label = `${stock} / ${ek}`;
  const sku = item.repricer_sku;
  if (!sku || !navigate) return label;
  return (
    <Link
      component="button"
      variant="body2"
      sx={{ textAlign: "right", display: "block", width: "100%" }}
      title={`Open FBA repricer (${sku})`}
      onClick={() =>
        navigate("/multi-repricer", {
          state: { platform: "fba", sku },
        })
      }
    >
      {label}
    </Link>
  );
};

const renderWatchlistStatus = (item) => {
  if (item.archived_at) {
    return (
      <Chip label="Archived" size="small" color="default" variant="outlined" />
    );
  }

  const repricerChip = item.repricer_article_id ? (
    <Chip label="In repricer" size="small" color="info" variant="outlined" />
  ) : null;

  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {repricerChip}
      {item.source === "keepa" && (
        <Chip label="Keepa only" size="small" variant="outlined" />
      )}
      {item.keepa_fetch_status && (
        <Chip
          label={
            KEEPA_FETCH_STATUS_LABELS[item.keepa_fetch_status] ||
            item.keepa_fetch_status
          }
          size="small"
          color="warning"
          variant="outlined"
        />
      )}
      {item.keepa_scanned_at && (
        <Chip
          label="Analyzed"
          size="small"
          variant="outlined"
          color="secondary"
        />
      )}
      {item.in_qogita_catalog === true && (
        <Chip
          label="In Qogita catalog"
          size="small"
          color="success"
          variant="outlined"
        />
      )}
      {item.in_qogita_catalog === false && (
        <Chip
          label="Not in Qogita catalog"
          size="small"
          color="error"
          variant="outlined"
        />
      )}
      {item.wholesale_best_seller &&
        item.in_qogita_catalog === false &&
        item.wholesale_best_price != null && (
          <Chip
            label={`Wholesale: ${item.wholesale_best_seller}`}
            size="small"
            color="default"
            variant="outlined"
          />
        )}
      {item.are_targets_met && (
        <Chip label="Targets met" size="small" color="primary" />
      )}
    </Box>
  );
};

const normalizeCategoriesResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.categories)) return data.categories;
  return [];
};

const flattenCategoryPathOptions = (nodes, parentSlugs = []) => {
  const options = [];
  if (!Array.isArray(nodes)) return options;
  for (const node of nodes) {
    const slug = node?.slug || node?.name;
    if (!slug) continue;
    const path = [...parentSlugs, String(slug).trim()].filter(Boolean);
    const label =
      node?.name && node.name !== slug
        ? `${node.name} (${path.join(" / ")})`
        : path.join(" / ");
    options.push({ label, path });
    const children = node.children || node.subcategories || [];
    if (children.length) {
      options.push(...flattenCategoryPathOptions(children, path));
    }
  }
  return options;
};

const applyDownloadPrefsToForm = (prefs) => ({
  category_slug: Array.isArray(prefs?.category_slug) ? prefs.category_slug : [],
  max_estimated_delivery_time:
    prefs?.max_estimated_delivery_time != null
      ? String(prefs.max_estimated_delivery_time)
      : "",
  brand_names: Array.isArray(prefs?.brand_names) ? prefs.brand_names : [],
});

const parseCategorySlugPathInput = (text) => {
  if (!text || !String(text).trim()) return [];
  return String(text)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
};

const PIPELINE_POLL_RUNNING_MS = 120_000;
const PIPELINE_POLL_WAITING_MS = 240_000;
const CATALOG_POLL_PENDING_MS = 60_000;

const formatEta = (seconds) => {
  if (seconds == null || seconds < 0) return "—";
  if (seconds === 0) return "soon";
  if (seconds < 60) return `~${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `~${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `~${hours}h ${minutes % 60}m`;
};

const pipelineJobLabel = (type) => {
  if (type === "fetch_products") return "Fetch Products";
  if (type === "sync_asins") return "Sync ASINs";
  return type || "—";
};

const pipelineStatusLabel = (status) => {
  switch (status) {
    case "running":
      return "Running";
    case "waiting_tokens":
      return "Waiting for Keepa tokens";
    case "completed":
      return "Completed";
    case "stopped":
      return "Stopped";
    case "failed":
      return "Failed";
    default:
      return "Idle";
  }
};

const QogitaWatchlistTab = () => {
  const navigate = useNavigate();
  const savedPrefs = loadWatchlistPrefs();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [syncingAsins, setSyncingAsins] = useState(false);
  const [fetchingProducts, setFetchingProducts] = useState(false);
  const [analyzingProducts, setAnalyzingProducts] = useState(false);
  const [queueStatus, setQueueStatus] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [search, setSearch] = useState(savedPrefs.search);
  const [qogitaStatus, setQogitaStatus] = useState(
    savedPrefs.qogitaStatus || "all",
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(savedPrefs.pageSize);
  const [totalItems, setTotalItems] = useState(0);

  const [addOpen, setAddOpen] = useState(false);
  const [addAsinOpen, setAddAsinOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({
    gtin: "",
    target_price: "",
    target_quantity: "",
  });
  const [asinForm, setAsinForm] = useState({
    asin: "",
    target_quantity: "",
  });
  const [editingGtin, setEditingGtin] = useState(null);
  const [eanListDialogItem, setEanListDialogItem] = useState(null);
  const [rowActionKey, setRowActionKey] = useState(null);
  const [addingAsin, setAddingAsin] = useState(false);

  const [qogitaCronStatus, setQogitaCronStatus] = useState(null);
  const [keepaCronStatus, setKeepaCronStatus] = useState(null);
  const [cronSaving, setCronSaving] = useState(false);
  const [webhookTesting, setWebhookTesting] = useState(false);
  const [cronForm, setCronForm] = useState({
    qogitaSchedule: savedPrefs.qogitaSchedule,
    keepaSchedule: savedPrefs.keepaSchedule,
    discordWebhook: savedPrefs.discordWebhook,
    targetQuantity: savedPrefs.targetQuantity,
  });
  const [keepaQueryJson, setKeepaQueryJson] = useState(
    savedPrefs.keepaQuerySelection ?? "",
  );
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [pipelineStopping, setPipelineStopping] = useState(false);
  const [catalogStatus, setCatalogStatus] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogRequesting, setCatalogRequesting] = useState(false);
  const [catalogPrefsSaving, setCatalogPrefsSaving] = useState(false);
  const [catalogDownloadPrefs, setCatalogDownloadPrefs] = useState(() =>
    applyDownloadPrefsToForm(null),
  );
  const [categoryPathOptions, setCategoryPathOptions] = useState([]);
  const [categoryPathManual, setCategoryPathManual] = useState("");
  const [categoriesLoadError, setCategoriesLoadError] = useState(null);

  const pipelineActive = Boolean(pipelineStatus?.active);
  const catalogRequestsRemaining = catalogStatus?.requests_remaining;
  const catalogRequestsLast24h = catalogStatus?.requests_last_24h;
  const qogitaCronFiresPerDay = estimateCronFiresPerDay(cronForm.qogitaSchedule);
  const qogitaCronOverBudget =
    qogitaCronFiresPerDay != null &&
    qogitaCronFiresPerDay > CATALOG_DOWNLOAD_MAX_PER_24H;
  const catalogPending =
    catalogStatus?.latest_download?.status === "pending" ||
    catalogStatus?.latest_download?.status === "importing";
  const catalogImporting =
    catalogStatus?.latest_download?.status === "importing";
  const catalogRequestedAt = catalogStatus?.latest_download?.requested_at
    ? new Date(catalogStatus.latest_download.requested_at)
    : null;
  const catalogPendingAgeMs = catalogRequestedAt
    ? Date.now() - catalogRequestedAt.getTime()
    : 0;
  const catalogPendingStale =
    catalogPending && catalogPendingAgeMs > 5 * 60 * 1000;
  const catalogPendingExpiresAt = catalogStatus?.pending_expires_at
    ? new Date(catalogStatus.pending_expires_at)
    : null;

  const updateCronForm = useCallback((partial) => {
    setCronForm((prev) => {
      const next = { ...prev, ...partial };
      saveWatchlistPrefs({
        qogitaSchedule: next.qogitaSchedule,
        keepaSchedule: next.keepaSchedule,
        discordWebhook: next.discordWebhook,
        targetQuantity: next.targetQuantity,
      });
      return next;
    });
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await qogitaApi.listWatchlist(
        page + 1,
        pageSize,
        search,
        qogitaStatus,
      );
      setItems(response.data.items || []);
      setTotalItems(response.data.total || 0);
      setError(null);
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Failed to load watchlist",
      );
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, qogitaStatus]);

  const loadQueueStatus = useCallback(async () => {
    try {
      const response = await keepaApi.getQueueStatus();
      setQueueStatus(response.data);
    } catch {
      // optional — no Keepa key yet
    }
  }, []);

  const loadPipelineStatus = useCallback(async () => {
    try {
      const response = await keepaApi.getPipelineStatus();
      setPipelineStatus(response.data);
      if (response.data?.pipeline) {
        setQueueStatus(response.data.pipeline);
      }
      return response.data;
    } catch {
      return null;
    }
  }, []);

  const loadCatalogStatus = useCallback(async () => {
    try {
      const response = await qogitaApi.getCatalogStatus();
      setCatalogStatus(response.data);
      if (response.data?.download_prefs) {
        setCatalogDownloadPrefs(
          applyDownloadPrefsToForm(response.data.download_prefs),
        );
      }
      return response.data;
    } catch {
      return null;
    }
  }, []);

  const loadCatalogDownloadPrefs = useCallback(async () => {
    try {
      const response = await qogitaApi.getCatalogDownloadPrefs();
      setCatalogDownloadPrefs(applyDownloadPrefsToForm(response.data));
      return response.data;
    } catch {
      return null;
    }
  }, []);

  const loadCatalogCategories = useCallback(async () => {
    try {
      setCategoriesLoadError(null);
      const response = await qogitaApi.listCatalogCategories();
      const nodes = normalizeCategoriesResponse(response.data);
      setCategoryPathOptions(flattenCategoryPathOptions(nodes));
    } catch (err) {
      setCategoriesLoadError(
        err.response?.data?.detail ||
          err.message ||
          "Could not load Qogita categories",
      );
      setCategoryPathOptions([]);
    }
  }, []);

  const loadDefaultKeepaQuery = useCallback(async () => {
    try {
      const response = await keepaApi.getDefaultQuerySelection();
      const formatted = formatKeepaQuerySelection(response.data);
      setKeepaQueryJson(formatted);
      saveWatchlistPrefs({ keepaQuerySelection: formatted });
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to load default Keepa query",
      );
    }
  }, []);

  const loadCronStatus = useCallback(async () => {
    try {
      const [qogitaRes, keepaRes] = await Promise.all([
        priceAlertsApi.getQogitaCronStatus(),
        priceAlertsApi.getKeepaCronStatus(),
      ]);
      setQogitaCronStatus(qogitaRes.data);
      setKeepaCronStatus(keepaRes.data);
      const qArgs = qogitaRes.data?.arguments || {};
      const kArgs = keepaRes.data?.arguments || {};
      setCronForm((prev) => {
        const next = {
          ...prev,
          qogitaSchedule: qogitaRes.data?.expression || prev.qogitaSchedule,
          keepaSchedule: keepaRes.data?.expression || prev.keepaSchedule,
          discordWebhook: qArgs.discord_webhook || prev.discordWebhook,
          targetQuantity:
            qArgs.target_quantity ??
            kArgs.target_quantity ??
            prev.targetQuantity,
        };
        saveWatchlistPrefs({
          qogitaSchedule: next.qogitaSchedule,
          keepaSchedule: next.keepaSchedule,
          discordWebhook: next.discordWebhook,
          targetQuantity: next.targetQuantity,
        });
        return next;
      });
    } catch {
      // cron endpoints optional until backend deployed
    }
  }, []);

  useEffect(() => {
    fetchItems();
    loadQueueStatus();
    loadCronStatus();
    loadPipelineStatus();
    loadCatalogStatus();
    loadCatalogDownloadPrefs();
    loadCatalogCategories();
  }, [
    fetchItems,
    loadQueueStatus,
    loadCronStatus,
    loadPipelineStatus,
    loadCatalogStatus,
    loadCatalogDownloadPrefs,
    loadCatalogCategories,
  ]);

  useEffect(() => {
    if (keepaQueryJson.trim()) return;
    loadDefaultKeepaQuery();
  }, [keepaQueryJson, loadDefaultKeepaQuery]);

  useEffect(() => {
    if (!catalogPending) return undefined;
    const intervalId = setInterval(async () => {
      const status = await loadCatalogStatus();
      if (status?.latest_download?.status === "completed") {
        setSuccess(
          `Qogita catalog imported: ${
            status.catalog_gtin_count?.toLocaleString() ?? 0
          } GTINs.`,
        );
        await fetchItems();
      } else if (status?.latest_download?.status === "failed") {
        setError(
          status.latest_download.error_message ||
            "Qogita catalog download failed",
        );
      }
    }, CATALOG_POLL_PENDING_MS);
    return () => clearInterval(intervalId);
  }, [catalogPending, loadCatalogStatus, fetchItems]);

  useEffect(() => {
    if (!pipelineActive) return undefined;
    const pollMs =
      pipelineStatus?.status === "waiting_tokens"
        ? PIPELINE_POLL_WAITING_MS
        : PIPELINE_POLL_RUNNING_MS;
    const intervalId = setInterval(async () => {
      const status = await loadPipelineStatus();
      if (status && !status.active) {
        await loadQueueStatus();
        if (status.status === "completed") {
          setSuccess(
            status.job_type === "sync_asins"
              ? `Keepa sync completed (${status.processed} ASINs stored).`
              : `Keepa fetch completed (${status.processed} products).`,
          );
          await fetchItems();
        } else if (status.status === "failed") {
          setError(status.error_message || "Keepa pipeline failed");
        }
      }
    }, pollMs);
    return () => clearInterval(intervalId);
  }, [
    pipelineActive,
    pipelineStatus?.status,
    loadPipelineStatus,
    loadQueueStatus,
    fetchItems,
  ]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      await Promise.all([fetchItems(), loadQueueStatus()]);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const handleSyncAsins = async () => {
    try {
      setSyncingAsins(true);
      setError(null);
      let query_selection = null;
      try {
        query_selection = parseKeepaQuerySelection(keepaQueryJson);
      } catch (parseErr) {
        setError(parseErr.message || "Invalid Keepa query JSON");
        return;
      }
      const response = await keepaApi.startPipelineSyncAsins({
        replace_queue: true,
        chain_fetch: true,
        query_selection,
      });
      setPipelineStatus(response.data);
      setSuccess(
        response.data?.message ||
          "Keepa import started — Sync ASINs then Fetch Products (token-aware).",
      );
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Keepa sync-asins failed",
      );
    } finally {
      setSyncingAsins(false);
    }
  };

  const handleFetchProducts = async () => {
    try {
      setFetchingProducts(true);
      setError(null);
      const response = await keepaApi.startPipelineFetchProducts({
        limit: 100,
        re_fetch: true,
      });
      setPipelineStatus(response.data);
      const pending = response.data?.pipeline?.pending_fetch;
      setSuccess(
        pending != null
          ? `Keepa fetch started — ${pending.toLocaleString()} ASINs queued (re-fetch all).`
          : "Keepa fetch started — processes all ASINs with token-aware pauses.",
      );
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Keepa fetch-products failed",
      );
    } finally {
      setFetchingProducts(false);
    }
  };

  const handleStopPipeline = async () => {
    try {
      setPipelineStopping(true);
      setError(null);
      const response = await keepaApi.stopPipeline();
      await loadPipelineStatus();
      setSuccess(
        response.data?.message ||
          "Keepa pipeline force-stopped. No rows deleted — catalog download is safe.",
      );
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Failed to stop pipeline",
      );
    } finally {
      setPipelineStopping(false);
    }
  };

  const handleAnalyzeProducts = async () => {
    try {
      setAnalyzingProducts(true);
      setError(null);
      const targetQuantity = Number(cronForm.targetQuantity) || 10;

      const response = await keepaApi.analyzeProducts({
        target_quantity: targetQuantity,
      });
      const stats = response.data;

      setSuccess(
        `Analyzed ${stats.stored || 0} products by EAN (${stats.skipped || 0} skipped` +
          (stats.skipped_analyze > 0
            ? `, ${stats.skipped_analyze} without EK metrics`
            : "") +
          (stats.ek_changed > 0 ? `, ${stats.ek_changed} EK updated` : "") +
          (stats.margin_changed > 0
            ? `, ${stats.margin_changed} margin tier updates`
            : "") +
          (stats.in_catalog != null
            ? `, ${stats.in_catalog} in catalog, ${stats.not_in_catalog} not in catalog`
            : "") +
          ").",
      );
      setQueueStatus(stats);
      await fetchItems();
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Keepa analyze failed",
      );
    } finally {
      setAnalyzingProducts(false);
    }
  };

  const handleAdd = async () => {
    try {
      setLoading(true);
      await qogitaApi.addWatchlistItem({
        gtin: form.gtin.trim(),
        target_price: form.target_price ? Number(form.target_price) : undefined,
        target_quantity: form.target_quantity
          ? Number(form.target_quantity)
          : undefined,
      });
      setAddOpen(false);
      setForm({ gtin: "", target_price: "", target_quantity: "" });
      setSuccess("Item added to local watchlist.");
      await fetchItems();
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Failed to add item",
      );
    } finally {
      setLoading(false);
    }
  };

  const rowActionBusy = (gtin, action) => rowActionKey === `${gtin}:${action}`;

  const handleRowFetch = async (item) => {
    if (!item.asin) {
      setError(
        "This row has no ASIN — fetch is only available after Keepa sync.",
      );
      return;
    }
    const key = `${item.gtin}:fetch`;
    try {
      setRowActionKey(key);
      setError(null);
      const response = await keepaApi.fetchProducts({
        asins: [item.asin],
        limit: 1,
      });
      const stats = response.data;
      setSuccess(
        `Fetched ${stats.fetched || 0} product(s) for ${item.asin}` +
          (stats.skipped_no_ean
            ? ` (${stats.skipped_no_ean} without EAN)`
            : "") +
          ".",
      );
      await fetchItems();
      await loadQueueStatus();
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Keepa fetch failed",
      );
    } finally {
      setRowActionKey(null);
    }
  };

  const handleRowAnalyze = async (item) => {
    const key = `${item.gtin}:analyze`;
    try {
      setRowActionKey(key);
      setError(null);
      const targetQuantity = Number(cronForm.targetQuantity) || 10;
      const response = await keepaApi.analyzeProducts({
        gtins: [item.gtin],
        target_quantity: targetQuantity,
      });
      const stats = response.data;
      setSuccess(
        `Analyzed ${item.gtin}: ${stats.stored || 0} stored` +
          (stats.skipped ? `, ${stats.skipped} skipped` : "") +
          ".",
      );
      await fetchItems();
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Keepa analyze failed",
      );
    } finally {
      setRowActionKey(null);
    }
  };

  const handleAddAsin = async () => {
    const asin = asinForm.asin.trim().toUpperCase();
    if (!asin) return;
    try {
      setAddingAsin(true);
      setError(null);
      const response = await keepaApi.addAsin({
        asin,
        target_quantity: asinForm.target_quantity
          ? Number(asinForm.target_quantity)
          : Number(cronForm.targetQuantity) || 10,
        analyze: true,
      });
      const result = response.data;
      const gtinCount = result.gtins?.length || 0;
      setAddAsinOpen(false);
      setAsinForm({ asin: "", target_quantity: "" });
      setSuccess(
        `ASIN ${result.asin} added — fetched ${result.fetch?.fetched || 0} product(s)` +
          (gtinCount ? `, ${gtinCount} EAN row(s)` : "") +
          (result.analyze?.stored != null
            ? `, analyzed ${result.analyze.stored}`
            : "") +
          ".",
      );
      await fetchItems();
      await loadQueueStatus();
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Failed to add ASIN",
      );
    } finally {
      setAddingAsin(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading(true);
      await qogitaApi.updateWatchlistItem(editingGtin, {
        target_price: form.target_price ? Number(form.target_price) : undefined,
        target_quantity: form.target_quantity
          ? Number(form.target_quantity)
          : undefined,
      });
      setEditOpen(false);
      setEditingGtin(null);
      setSuccess("Watchlist item updated.");
      await fetchItems();
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Failed to update item",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (gtin) => {
    if (
      !window.confirm(
        `Archive ${gtin}? It will be hidden and ignored by Keepa sync and analyze. You can restore it from the Archived filter.`,
      )
    ) {
      return;
    }
    try {
      setLoading(true);
      await qogitaApi.deleteWatchlistItem(gtin);
      setSuccess("Item archived.");
      await fetchItems();
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Failed to archive item",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (gtin) => {
    try {
      setLoading(true);
      await qogitaApi.restoreWatchlistItem(gtin);
      setSuccess("Item restored.");
      await fetchItems();
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Failed to restore item",
      );
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (item) => {
    setEditingGtin(item.gtin);
    setForm({
      gtin: item.gtin,
      target_price: item.target_price ?? "",
      target_quantity: item.target_quantity ?? "",
    });
    setEditOpen(true);
  };

  const cronBody = () => ({
    target_quantity: Number(cronForm.targetQuantity),
    reanalyze_limit: 500,
    refresh_limit: 200,
  });

  const handleTestDiscordWebhook = async () => {
    const url = cronForm.discordWebhook?.trim();
    if (!url) {
      setError("Enter a Discord webhook URL first.");
      return;
    }
    try {
      setWebhookTesting(true);
      setError(null);
      await priceAlertsApi.testDiscordWebhook(url);
      setSuccess("Discord webhook test sent — check your channel.");
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Discord webhook test failed",
      );
    } finally {
      setWebhookTesting(false);
    }
  };

  const handleStartQogitaCron = async () => {
    if (qogitaCronOverBudget) {
      setError(
        `Qogita schedule would trigger about ${qogitaCronFiresPerDay} catalog-related runs per day (limit ${CATALOG_DOWNLOAD_MAX_PER_24H}). Slow down the cron before starting.`,
      );
      return;
    }
    try {
      setCronSaving(true);
      setError(null);
      await priceAlertsApi.startQogitaCron({
        schedule: cronForm.qogitaSchedule,
        discord_webhook: cronForm.discordWebhook || null,
        ...cronBody(),
      });
      setSuccess("Qogita sync cron started.");
      await loadCronStatus();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to start Qogita cron",
      );
    } finally {
      setCronSaving(false);
    }
  };

  const handleStopQogitaCron = async () => {
    try {
      setCronSaving(true);
      await priceAlertsApi.stopQogitaCron();
      setSuccess("Qogita sync cron stopped.");
      await loadCronStatus();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to stop Qogita cron",
      );
    } finally {
      setCronSaving(false);
    }
  };

  const handleStartKeepaCron = async () => {
    try {
      setCronSaving(true);
      setError(null);
      await priceAlertsApi.startKeepaCron({
        schedule: cronForm.keepaSchedule,
        ...cronBody(),
      });
      setSuccess("Keepa price sync cron started.");
      await loadCronStatus();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to start Keepa cron",
      );
    } finally {
      setCronSaving(false);
    }
  };

  const handleStopKeepaCron = async () => {
    try {
      setCronSaving(true);
      await priceAlertsApi.stopKeepaCron();
      setSuccess("Keepa price sync cron stopped.");
      await loadCronStatus();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to stop Keepa cron",
      );
    } finally {
      setCronSaving(false);
    }
  };

  const handleSetupCatalogWebhook = async () => {
    try {
      setCatalogLoading(true);
      setError(null);
      const response = await qogitaApi.setupCatalogWebhook();
      setCatalogStatus(response.data);
      setSuccess("Qogita catalog webhook registered.");
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to register Qogita webhook",
      );
    } finally {
      setCatalogLoading(false);
    }
  };

  const handleSaveCatalogDownloadPrefs = async () => {
    try {
      setCatalogPrefsSaving(true);
      setError(null);
      const categoryFromManual = parseCategorySlugPathInput(categoryPathManual);
      const category_slug =
        categoryFromManual.length > 0
          ? categoryFromManual
          : catalogDownloadPrefs.category_slug;
      const maxRaw = catalogDownloadPrefs.max_estimated_delivery_time;
      const payload = {
        category_slug,
        brand_names: catalogDownloadPrefs.brand_names,
      };
      if (maxRaw === "" || maxRaw == null) {
        payload.max_estimated_delivery_time = null;
      } else {
        payload.max_estimated_delivery_time = Number(maxRaw);
      }
      const response = await qogitaApi.saveCatalogDownloadPrefs(payload);
      setCatalogDownloadPrefs(applyDownloadPrefsToForm(response.data));
      setCategoryPathManual("");
      setSuccess("Catalog download preferences saved.");
      await loadCatalogStatus();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to save catalog download preferences",
      );
    } finally {
      setCatalogPrefsSaving(false);
    }
  };

  const handleRequestCatalogDownload = async () => {
    try {
      setCatalogRequesting(true);
      setError(null);
      const discord = cronForm.discordWebhook?.trim() || undefined;
      const response = await qogitaApi.requestCatalogDownload(
        discord ? { discord_webhook: discord } : {},
      );
      setCatalogStatus(response.data);
      setSuccess(
        "Qogita catalog download requested — when the CSV arrives, analyze + Discord run automatically (once per request).",
      );
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to request Qogita catalog",
      );
    } finally {
      setCatalogRequesting(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {typeof error === "string" ? error : JSON.stringify(error)}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Qogita catalog (GTIN index)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Register a webhook, save download filters, then request a catalog CSV
          from Qogita. Imports update the local GTIN index used for watchlist
          catalog labels.
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">
              GTINs loaded
            </Typography>
            <Typography variant="body1">
              {catalogStatus?.catalog_loaded
                ? catalogStatus.catalog_gtin_count?.toLocaleString()
                : "—"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">
              Webhook
            </Typography>
            <Typography variant="body2">
              {catalogStatus?.webhook_configured ? "Configured" : "Not set up"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {catalogStatus?.callback_url || "—"}
            </Typography>
            {catalogStatus?.localhost_warning && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {catalogStatus.localhost_warning}
              </Alert>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">
              Download budget (24h)
            </Typography>
            <Typography variant="body2">
              {catalogRequestsLast24h != null
                ? `${catalogRequestsLast24h} used`
                : "—"}
              {catalogRequestsRemaining != null
                ? ` · ${catalogRequestsRemaining} remaining`
                : ""}
              {catalogRequestsRemaining != null
                ? ` (max ${CATALOG_DOWNLOAD_MAX_PER_24H})`
                : ""}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">
              Latest download
            </Typography>
            <Typography variant="body2">
              {catalogStatus?.latest_download?.status || "—"}
              {catalogStatus?.latest_download?.gtin_count != null
                ? ` · ${catalogStatus.latest_download.gtin_count.toLocaleString()} GTINs`
                : ""}
            </Typography>
            {catalogPending && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
              >
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  {catalogImporting
                    ? "Importing CSV (full catalog can take several minutes)…"
                    : catalogPendingStale
                      ? "Webhook overdue — you can retry the request (Qogita does not resend missed webhooks)."
                      : "Waiting for Qogita webhook (usually 2–3 minutes)…"}
                </Typography>
              </Box>
            )}
            {catalogPending && catalogPendingExpiresAt && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Auto-fails at {catalogPendingExpiresAt.toLocaleTimeString()} if
                no webhook arrives.
              </Typography>
            )}
            {catalogStatus?.latest_download?.status === "failed" &&
              catalogStatus?.latest_download?.error_message && (
                <Typography variant="caption" color="error" display="block">
                  {catalogStatus.latest_download.error_message}
                </Typography>
              )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Download preferences
            </Typography>
            {categoriesLoadError && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {categoriesLoadError} — enter a slug path manually below.
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {categoryPathOptions.length > 0 ? (
                  <>
                    <Autocomplete
                      size="small"
                      options={categoryPathOptions}
                      getOptionLabel={(opt) =>
                        typeof opt === "string" ? opt : opt.label
                      }
                      value={
                        categoryPathManual
                          ? null
                          : categoryPathOptions.find(
                              (opt) =>
                                JSON.stringify(opt.path) ===
                                JSON.stringify(
                                  catalogDownloadPrefs.category_slug,
                                ),
                            ) || null
                      }
                      onChange={(_, option) => {
                        setCategoryPathManual("");
                        setCatalogDownloadPrefs((prev) => ({
                          ...prev,
                          category_slug: option?.path || [],
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Category slug path"
                          helperText="Pick from Qogita categories, or override below"
                        />
                      )}
                    />
                    <TextField
                      size="small"
                      fullWidth
                      sx={{ mt: 1 }}
                      label="Manual slug path (optional)"
                      placeholder="health-beauty,fragrance"
                      value={categoryPathManual}
                      onChange={(e) => setCategoryPathManual(e.target.value)}
                      helperText="Comma-separated slugs override the picker when saved"
                    />
                  </>
                ) : (
                  <TextField
                    size="small"
                    fullWidth
                    label="Category slug path"
                    placeholder="health-beauty,fragrance"
                    value={
                      categoryPathManual ||
                      catalogDownloadPrefs.category_slug.join(",")
                    }
                    onChange={(e) => {
                      setCategoryPathManual(e.target.value);
                      setCatalogDownloadPrefs((prev) => ({
                        ...prev,
                        category_slug: parseCategorySlugPathInput(
                          e.target.value,
                        ),
                      }));
                    }}
                    helperText="Comma-separated slugs (parent to child). Empty = full catalog."
                  />
                )}
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  label="Max delivery (weeks)"
                  value={catalogDownloadPrefs.max_estimated_delivery_time}
                  onChange={(e) =>
                    setCatalogDownloadPrefs((prev) => ({
                      ...prev,
                      max_estimated_delivery_time: e.target.value,
                    }))
                  }
                  helperText="Optional; leave empty for no limit"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  size="small"
                  multiple
                  freeSolo
                  options={[]}
                  value={catalogDownloadPrefs.brand_names}
                  onChange={(_, value) =>
                    setCatalogDownloadPrefs((prev) => ({
                      ...prev,
                      brand_names: value.map((v) => String(v).trim()).filter(Boolean),
                    }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Brand names"
                      helperText="Optional — leave empty for all brands (no default filter)"
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1, mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={catalogPrefsSaving}
                onClick={handleSaveCatalogDownloadPrefs}
              >
                {catalogPrefsSaving ? "Saving…" : "Save download prefs"}
              </Button>
              {catalogDownloadPrefs.brand_names?.length > 0 && (
                <Button
                  variant="text"
                  size="small"
                  disabled={catalogPrefsSaving}
                  onClick={async () => {
                    setCatalogDownloadPrefs((prev) => ({
                      ...prev,
                      brand_names: [],
                    }));
                    try {
                      setCatalogPrefsSaving(true);
                      const response = await qogitaApi.saveCatalogDownloadPrefs({
                        category_slug: catalogDownloadPrefs.category_slug,
                        brand_names: [],
                        max_estimated_delivery_time:
                          catalogDownloadPrefs.max_estimated_delivery_time ===
                            "" ||
                          catalogDownloadPrefs.max_estimated_delivery_time ==
                            null
                            ? null
                            : Number(
                                catalogDownloadPrefs.max_estimated_delivery_time,
                              ),
                      });
                      setCatalogDownloadPrefs(
                        applyDownloadPrefsToForm(response.data),
                      );
                      setSuccess("Brand filter cleared.");
                    } catch (err) {
                      setError(
                        err.response?.data?.detail ||
                          err.message ||
                          "Failed to clear brands",
                      );
                    } finally {
                      setCatalogPrefsSaving(false);
                    }
                  }}
                >
                  Clear brands
                </Button>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                size="small"
                disabled={catalogLoading || catalogRequesting}
                onClick={handleSetupCatalogWebhook}
              >
                {catalogLoading ? "Setting up…" : "Setup webhook"}
              </Button>
              <Button
                variant="contained"
                size="small"
                disabled={
                  catalogLoading ||
                  catalogRequesting ||
                  (catalogPending && !catalogPendingStale) ||
                  !catalogStatus?.webhook_configured ||
                  catalogRequestsRemaining === 0
                }
                onClick={handleRequestCatalogDownload}
              >
                {catalogRequesting
                  ? "Requesting…"
                  : catalogPending && !catalogPendingStale
                    ? "Download pending…"
                    : catalogPendingStale
                      ? "Retry catalog request"
                      : "Request catalog"}
              </Button>
              <Button variant="text" size="small" onClick={loadCatalogStatus}>
                Refresh status
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Scheduled jobs
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Qogita cron syncs stock/prices, alerts Discord when targets are newly
          met, then re-analyzes and exports changed EK prices. Keepa cron
          refreshes Amazon selling prices and exports when values change.
        </Typography>
        <MarginTierInfo sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Qogita sync (Berlin TZ)
            </Typography>
            <CronBuilderBox sx={{ mb: 1 }}>
              <Cron
                value={cronForm.qogitaSchedule}
                onChange={(value) =>
                  updateCronForm({
                    qogitaSchedule: resolveCronValue(
                      value,
                      cronForm.qogitaSchedule,
                    ),
                  })
                }
                showResultText
                showResultCron
                locale="en"
              />
            </CronBuilderBox>
            {qogitaCronOverBudget ? (
              <Alert severity="error" sx={{ mb: 1 }}>
                This schedule would fire about {qogitaCronFiresPerDay} times per
                24h (limit {CATALOG_DOWNLOAD_MAX_PER_24H}). Start is disabled —
                pick a slower schedule (e.g. every few hours) before starting.
              </Alert>
            ) : (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 1 }}
              >
                Est. runs per day:{" "}
                {qogitaCronFiresPerDay != null ? qogitaCronFiresPerDay : "—"} /{" "}
                {CATALOG_DOWNLOAD_MAX_PER_24H}
                {catalogRequestsRemaining != null
                  ? ` · catalog downloads remaining (24h): ${catalogRequestsRemaining}`
                  : ""}
              </Typography>
            )}
            {qogitaCronOverBudget && (
              <Typography
                variant="caption"
                color="error"
                display="block"
                sx={{ mb: 1 }}
              >
                Est. runs per day: {qogitaCronFiresPerDay} /{" "}
                {CATALOG_DOWNLOAD_MAX_PER_24H}
                {catalogRequestsRemaining != null
                  ? ` · catalog downloads remaining (24h): ${catalogRequestsRemaining}`
                  : ""}
              </Typography>
            )}
            <TextField
              size="small"
              fullWidth
              label="Discord webhook (targets met)"
              value={cronForm.discordWebhook}
              onChange={(e) =>
                updateCronForm({ discordWebhook: e.target.value })
              }
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                disabled={webhookTesting || !cronForm.discordWebhook?.trim()}
                onClick={handleTestDiscordWebhook}
              >
                Test webhook
              </Button>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                size="small"
                disabled={cronSaving || qogitaCronOverBudget}
                onClick={handleStartQogitaCron}
                sx={
                  qogitaCronOverBudget
                    ? { opacity: 0.45, pointerEvents: "none" }
                    : undefined
                }
              >
                Start
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={cronSaving}
                onClick={handleStopQogitaCron}
              >
                Stop
              </Button>
              <Typography variant="caption" color="text.secondary">
                {qogitaCronStatus?.status === "active"
                  ? `Active · next ${qogitaCronStatus.next_run_at || "—"}`
                  : "Not active"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Keepa price sync (Berlin TZ)
            </Typography>
            <CronBuilderBox sx={{ mb: 1 }}>
              <Cron
                value={cronForm.keepaSchedule}
                onChange={(value) =>
                  updateCronForm({
                    keepaSchedule: resolveCronValue(
                      value,
                      cronForm.keepaSchedule,
                    ),
                  })
                }
                showResultText
                showResultCron
                locale="en"
              />
            </CronBuilderBox>
            <Box sx={{ display: "flex", gap: 2, mb: 1, alignItems: "center" }}>
              <TextField
                size="small"
                label="Target qty"
                type="number"
                value={cronForm.targetQuantity}
                onChange={(e) =>
                  updateCronForm({
                    targetQuantity: Number(e.target.value) || 10,
                  })
                }
                sx={{ width: 120 }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                size="small"
                color="secondary"
                disabled={cronSaving}
                onClick={handleStartKeepaCron}
              >
                Start
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={cronSaving}
                onClick={handleStopKeepaCron}
              >
                Stop
              </Button>
              <Typography variant="caption" color="text.secondary">
                {keepaCronStatus?.status === "active"
                  ? `Active · next ${keepaCronStatus.next_run_at || "—"}`
                  : "Not active"}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {(pipelineActive ||
        (pipelineStatus &&
          ["completed", "failed", "stopped"].includes(
            pipelineStatus.status,
          ))) && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              mb: 1,
            }}
          >
            <Typography variant="subtitle1">
              Keepa pipeline: {pipelineJobLabel(pipelineStatus?.job_type)} ·{" "}
              {pipelineStatusLabel(pipelineStatus?.status)}
            </Typography>
            {pipelineActive && (
              <Tooltip title="Stops Sync/Fetch immediately. Does not delete or prune any watchlist rows — safe before catalog download.">
                <span>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    disabled={pipelineStopping}
                    onClick={handleStopPipeline}
                  >
                    {pipelineStopping ? "Stopping…" : "Force stop"}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
          {pipelineStatus?.progress_percent != null && (
            <LinearProgress
              variant="determinate"
              value={pipelineStatus.progress_percent}
              sx={{ mb: 1.5, height: 8, borderRadius: 1 }}
            />
          )}
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Processed
              </Typography>
              <Typography variant="body2">
                {pipelineStatus?.processed ?? 0}
                {pipelineStatus?.total != null
                  ? ` / ${pipelineStatus.total}`
                  : ""}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Remaining
              </Typography>
              <Typography variant="body2">
                {pipelineStatus?.remaining ?? "—"}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Cron runs
              </Typography>
              <Typography variant="body2">
                {pipelineStatus?.run_count ?? 0}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Tokens left
              </Typography>
              <Typography variant="body2">
                {pipelineStatus?.tokens_left ?? "—"}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                Tokens used
              </Typography>
              <Typography variant="body2">
                {pipelineStatus?.tokens_consumed_total ?? 0}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="caption" color="text.secondary">
                {pipelineStatus?.status === "waiting_tokens"
                  ? "Resume in"
                  : "ETA"}
              </Typography>
              <Typography variant="body2">
                {pipelineStatus?.status === "waiting_tokens"
                  ? formatEta(pipelineStatus?.wait_seconds)
                  : formatEta(pipelineStatus?.estimated_seconds_remaining)}
              </Typography>
            </Grid>
          </Grid>
          {pipelineStatus?.status === "waiting_tokens" && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Keepa tokens refill at ~5/min. Cron checks every 30 minutes
              {pipelineStatus?.wait_until
                ? ` · next attempt ~${new Date(
                    pipelineStatus.wait_until,
                  ).toLocaleTimeString()}`
                : ""}
              . No need to keep this page open.
            </Typography>
          )}
          {pipelineStatus?.error_message && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {pipelineStatus.error_message}
            </Alert>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Keepa Product Finder query
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste the selection JSON from Keepa (page and perPage are ignored —
          sync fetches all matching ASINs with automatic pagination).
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={8}
          maxRows={20}
          value={keepaQueryJson}
          onChange={(e) => {
            const value = e.target.value;
            setKeepaQueryJson(value);
            saveWatchlistPrefs({ keepaQuerySelection: value });
          }}
          sx={{
            mb: 1,
            fontFamily: "monospace",
            "& textarea": { fontFamily: "monospace", fontSize: "0.8rem" },
          }}
        />
        <Button size="small" variant="text" onClick={loadDefaultKeepaQuery}>
          Reset to server default
        </Button>
      </Paper>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {queueStatus && (
          <Typography variant="body2" color="text.secondary">
            Keepa queue: {queueStatus.total} items · {queueStatus.pending_fetch}{" "}
            awaiting product fetch · {queueStatus.pending_analyze} awaiting
            analyze · {queueStatus.analyzed} analyzed
            {queueStatus.keepa_skipped > 0
              ? ` · ${queueStatus.keepa_skipped} skipped by Keepa`
              : ""}
          </Typography>
        )}
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" component="div">
          <strong>1. Keepa import</strong> — Sync ASINs (Product Finder →
          watchlist, prune after full success) then automatically Fetch
          Products. Use “Re-fetch only” if you need product data again without
          re-querying the finder.
        </Typography>
        <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
          <strong>2. Download Qogita catalog</strong> — Requests the CSV; when
          it arrives, analyze + Discord run once (retries do not re-send).
        </Typography>
        <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
          <strong>3. Analyze (by EAN)</strong> — Manual EK refresh only; does
          not send Discord. Prefer catalog download for alerts.
        </Typography>
      </Alert>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          label="Search name, GTIN or ASIN"
          value={search}
          onChange={(e) => {
            const value = e.target.value;
            setSearch(value);
            setPage(0);
            saveWatchlistPrefs({ search: value });
          }}
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="qogita-status-filter-label">Status filter</InputLabel>
          <Select
            labelId="qogita-status-filter-label"
            label="Status filter"
            value={qogitaStatus}
            onChange={(e) => {
              const value = e.target.value;
              setQogitaStatus(value);
              setPage(0);
              saveWatchlistPrefs({ qogitaStatus: value });
            }}
          >
            {QOGITA_STATUS_FILTERS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={
            refreshing ? <CircularProgress size={18} /> : <RefreshIcon />
          }
          onClick={handleRefresh}
          disabled={refreshing || loading}
        >
          Refresh
        </Button>
        <Tooltip title="Sync ASINs from Keepa Product Finder, then automatically fetch product data. After a full successful sync, prunes Keepa rows no longer in the finder (not for missing Qogita catalog).">
          <span>
            <Button
              variant="contained"
              startIcon={
                syncingAsins ? <CircularProgress size={18} /> : <ScanIcon />
              }
              onClick={handleSyncAsins}
              disabled={
                pipelineActive ||
                syncingAsins ||
                fetchingProducts ||
                analyzingProducts ||
                catalogRequesting
              }
            >
              1. Keepa import
            </Button>
          </span>
        </Tooltip>
        {pipelineActive && (
          <Tooltip title="Force-stop Keepa Sync/Fetch. Never deletes watchlist rows.">
            <span>
              <Button
                variant="outlined"
                color="warning"
                disabled={pipelineStopping}
                onClick={handleStopPipeline}
              >
                {pipelineStopping ? "Stopping…" : "Force stop Keepa"}
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Re-fetch Keepa product data for existing ASINs only (no finder sync).">
          <span>
            <Button
              variant="outlined"
              startIcon={
                fetchingProducts ? (
                  <CircularProgress size={18} />
                ) : (
                  <FetchIcon />
                )
              }
              onClick={handleFetchProducts}
              disabled={
                pipelineActive ||
                syncingAsins ||
                fetchingProducts ||
                analyzingProducts
              }
            >
              Re-fetch only
            </Button>
          </span>
        </Tooltip>
        <TextField
          size="small"
          label="Target qty (analyze)"
          type="number"
          value={cronForm.targetQuantity}
          onChange={(e) =>
            updateCronForm({
              targetQuantity: Number(e.target.value) || 10,
            })
          }
          sx={{ width: 140 }}
        />
        <Tooltip title="Request Qogita catalog CSV. When it arrives via webhook, analyze + Discord run automatically once (duplicate webhooks are ignored). Set Discord URL above first.">
          <span>
            <Button
              variant="outlined"
              color="primary"
              startIcon={
                catalogRequesting ? (
                  <CircularProgress size={18} />
                ) : (
                  <FetchIcon />
                )
              }
              onClick={handleRequestCatalogDownload}
              disabled={
                catalogLoading ||
                catalogRequesting ||
                (catalogPending && !catalogPendingStale) ||
                !catalogStatus?.webhook_configured ||
                catalogRequestsRemaining === 0 ||
                pipelineActive ||
                analyzingProducts
              }
            >
              {catalogRequesting
                ? "Requesting…"
                : catalogPending && !catalogPendingStale
                  ? "Catalog pending…"
                  : "2. Download Qogita catalog"}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Manual EK analyze by EAN only — does not send Discord. Catalog download already analyzes and alerts.">
          <span>
            <Button
              variant="contained"
              color="secondary"
              startIcon={
                analyzingProducts ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <AnalyzeIcon />
                )
              }
              onClick={handleAnalyzeProducts}
              disabled={
                syncingAsins ||
                fetchingProducts ||
                analyzingProducts ||
                catalogRequesting
              }
            >
              3. Analyze (by EAN)
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
        >
          Add GTIN
        </Button>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setAddAsinOpen(true)}
          disabled={pipelineActive || addingAsin}
        >
          Add ASIN
        </Button>
      </Box>
      <MarginTierInfo sx={{ mb: 2 }} />

      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>GTIN</TableCell>
              <TableCell>ASIN</TableCell>
              <TableCell
                align="right"
                title="Keepa eanList barcodes for this ASIN"
              >
                EANs
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell align="right">Avg VK (30d)</TableCell>
              <TableCell align="right">Fees</TableCell>
              <TableCell align="right">Target EK</TableCell>
              <TableCell align="right">Best wholesale</TableCell>
              <TableCell align="right">Qogita catalog</TableCell>
              <TableCell align="right">Qogita stock</TableCell>
              <TableCell
                align="right"
                title="Repricer stock / brutto buy price (FBA)"
              >
                Our stock / EK
              </TableCell>
              <TableCell align="right">Rank</TableCell>
              <TableCell align="right">Sold/mo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" sx={{ minWidth: 220 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={15} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={15} align="center">
                  No watchlist items. Run the Keepa pipeline or add a GTIN /
                  ASIN.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.gtin} hover>
                  <TableCell>{renderQogitaGtinLink(item)}</TableCell>
                  <TableCell>
                    {item.asin ? renderAmazonLink(item, item.asin) : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <KeepaEanCountButton
                      item={item}
                      onOpen={setEanListDialogItem}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    {item.name || "—"}
                  </TableCell>
                  <TableCell align="right">
                    {formatCents(item.avg30_new_price_cents)}
                  </TableCell>
                  <TableCell align="right">
                    {formatPrice(item.total_fees_eur)}
                  </TableCell>
                  <TableCell align="right">
                    {formatTargetEk(item)}
                    {formatTargetEkMarginCaption(item) && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        {formatTargetEkMarginCaption(item)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {item.wholesale_best_price != null
                      ? formatPrice(item.wholesale_best_price)
                      : "—"}
                    {item.wholesale_best_seller && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        {item.wholesale_best_seller}
                      </Typography>
                    )}
                    {item.wholesale_best_stock != null && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        stock {item.wholesale_best_stock}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {qogitaCatalogPrice(item) != null
                      ? formatPrice(qogitaCatalogPrice(item))
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {qogitaCatalogStock(item) ?? "—"}
                  </TableCell>
                  <TableCell align="right">
                    {renderRepricerInventory(item, navigate)}
                  </TableCell>
                  <TableCell align="right">
                    {item.avg30_sales_rank ?? "—"}
                  </TableCell>
                  <TableCell align="right">
                    {item.monthly_sold ?? "—"}
                  </TableCell>
                  <TableCell>{renderWatchlistStatus(item)}</TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        flexWrap: "nowrap",
                        gap: 0.25,
                      }}
                    >
                      {!item.archived_at && (
                        <>
                          <Tooltip title="Fetch product (Keepa)">
                            <span>
                              <IconButton
                                size="small"
                                disabled={
                                  !item.asin ||
                                  pipelineActive ||
                                  rowActionKey != null
                                }
                                onClick={() => handleRowFetch(item)}
                              >
                                {rowActionBusy(item.gtin, "fetch") ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <FetchIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Analyze (by EAN)">
                            <span>
                              <IconButton
                                size="small"
                                disabled={rowActionKey != null}
                                onClick={() => handleRowAnalyze(item)}
                              >
                                {rowActionBusy(item.gtin, "analyze") ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <AnalyzeIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <IconButton
                            size="small"
                            title="Edit targets"
                            onClick={() => openEditDialog(item)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {item.archived_at ? (
                        <IconButton
                          size="small"
                          title="Restore"
                          onClick={() => handleRestore(item.gtin)}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          title="Archive"
                          onClick={() => handleArchive(item.gtin)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            const size = parseInt(e.target.value, 10);
            setPageSize(size);
            setPage(0);
            saveWatchlistPrefs({ pageSize: size });
          }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>

      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Watchlist Item (Qogita)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Adds the GTIN on Qogita with your target price/qty. No Keepa step
            required — the watchlist cron still monitors wholesale targets and
            sends Discord alerts when targets are met.
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            label="GTIN / EAN"
            value={form.gtin}
            onChange={(e) => setForm({ ...form, gtin: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Target Price (EUR)"
            type="number"
            value={form.target_price}
            onChange={(e) => setForm({ ...form, target_price: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Target Quantity"
            type="number"
            value={form.target_quantity}
            onChange={(e) =>
              setForm({ ...form, target_quantity: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!form.gtin.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addAsinOpen}
        onClose={() => setAddAsinOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add ASIN (Keepa)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Registers the ASIN (or refreshes an existing row), fetches product
            data from Keepa, and analyzes catalog EANs.
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            label="Amazon ASIN"
            value={asinForm.asin}
            onChange={(e) =>
              setAsinForm({
                ...asinForm,
                asin: e.target.value.toUpperCase().replace(/\s/g, ""),
              })
            }
            inputProps={{ maxLength: 10 }}
            placeholder="B0XXXXXXXX"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Target Quantity (analyze)"
            type="number"
            value={asinForm.target_quantity}
            onChange={(e) =>
              setAsinForm({ ...asinForm, target_quantity: e.target.value })
            }
            placeholder={String(cronForm.targetQuantity || 10)}
            helperText="Leave empty to use the global target qty above"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddAsinOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddAsin}
            disabled={asinForm.asin.trim().length < 10 || addingAsin}
            startIcon={
              addingAsin ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            Add and Fetch
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Targets — {editingGtin}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Target Price (EUR)"
            type="number"
            value={form.target_price}
            onChange={(e) => setForm({ ...form, target_price: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Target Quantity"
            type="number"
            value={form.target_quantity}
            onChange={(e) =>
              setForm({ ...form, target_quantity: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(eanListDialogItem)}
        onClose={() => setEanListDialogItem(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Keepa EANs
          {eanListDialogItem?.asin ? ` · ${eanListDialogItem.asin}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {keepaEanCount(eanListDialogItem)} barcode(s) from Keepa
            {eanListDialogItem?.keepa_ean_catalog_gtins?.length
              ? ` · ${eanListDialogItem.keepa_ean_catalog_gtins.length} in wholesale catalog`
              : ""}
          </Typography>
          <List dense disablePadding>
            {(eanListDialogItem?.keepa_ean_list ?? []).map((ean) => {
              const inCatalog =
                eanListDialogItem?.keepa_ean_catalog_gtins?.includes(ean);
              const isRowGtin = ean === eanListDialogItem?.gtin;
              const catalogUrl = qogitaCatalogSearchUrl(ean);
              return (
                <ListItem key={ean} disableGutters>
                  <ListItemText
                    primary={
                      catalogUrl ? (
                        <Link
                          href={catalogUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                        >
                          {ean}
                        </Link>
                      ) : (
                        ean
                      )
                    }
                    secondary={
                      [
                        inCatalog ? "In catalog" : null,
                        isRowGtin ? "This row GTIN" : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || null
                    }
                  />
                  {inCatalog ? (
                    <Chip label="Catalog" size="small" color="success" />
                  ) : null}
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEanListDialogItem(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QogitaWatchlistTab;
